import { BrowserRouter, Navigate, Route, useSearchParams } from "react-router-dom";
import { Routes } from "@datadog/browser-rum-react/react-router-v7";
import { SessionProvider, useSession } from "./session.tsx";
import { Forgot, ResetPassword, SignIn, SignUp } from "./pages/Auth.tsx";
import { Invite } from "./pages/Invite.tsx";
import { Home, JoinServer } from "./pages/Home.tsx";
import { Server } from "./pages/Server.tsx";
import { Latency } from "./pages/Latency.tsx";
import { Settings } from "./pages/Settings.tsx";
import { Authorize } from "./pages/Authorize.tsx";
import { Share } from "./pages/Share.tsx";

function SignedOutOnly({ children }: { children: React.ReactNode }) {
  const { me } = useSession();
  const [params] = useSearchParams();
  if (me === undefined) return null;
  // Signed in (or just signed in): go where the page was opened for ("?next=", our own paths only).
  const next = params.get("next") ?? "/";
  return me ? <Navigate to={next.startsWith("/") && !next.startsWith("//") ? next : "/"} replace /> : <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/new" element={<Home create />} />
          <Route path="/join" element={<JoinServer />} />
          <Route path="/login" element={<SignedOutOnly><SignIn /></SignedOutOnly>} />
          <Route path="/signup" element={<SignedOutOnly><SignUp /></SignedOutOnly>} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/invite/:code" element={<Invite />} />
          <Route path="/share" element={<Share />} />
          {/* Settings are drawn by the same Server page (React keeps it mounted across these two routes), so the
              server's live connection carries on while they're open. */}
          <Route path="/s/:serverId/settings/:section?/:focus?" element={<Server />} />
          <Route path="/s/:serverId/apps/:slug?/:tab?" element={<Server />} />
          <Route path="/s/:serverId/bloom/:threadId" element={<Server />} />
          <Route path="/s/:serverId/:channelId?" element={<Server />} />
          <Route path="/s/:serverId/:channelId/:messageId" element={<Server />} />
          <Route path="/latency" element={<Latency />} />
          <Route path="/settings" element={<Settings />} />
          {/* The connector's OAuth consent screen (docs/connector.md). */}
          <Route path="/oauth/authorize" element={<Authorize />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
